import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';

import useJwtToken from '../config/auth/useJwtToken';
import { getOrganizationAppearance } from './appearanceApi';
import {
  ResolvedBranding,
  applyBrandToDocument,
  cacheBranding,
  platformBranding,
  readCachedBranding,
  resolveBranding,
} from './applyBrand';

/**
 * Applies the organization's branding to the whole application.
 *
 * Mounted above the router so it also covers the screens a guest can reach — the /m/j join
 * lobby, sign-in, sign-up, activation, password reset and post-meeting feedback all render
 * without a session. The previous implementation lived inside the Sidebar, so it could only ever
 * affect authenticated screens, and in practice only tinted the sidebar itself.
 *
 * Resolves the organization from the JWT. Without a session there is nothing to resolve, so
 * guest screens keep the platform look — see the note in the effect below.
 *
 * There is deliberately no platform-staff escape hatch here, unlike cm-frontend. In cm-frontend a
 * Convay-based admin edits *other* organizations, so its chrome must not adopt the tenant's brand;
 * in vcon-ui you are always acting inside your own organization, so your organization's branding
 * is always the right answer. A user who belongs to no organization gets organization_id: null
 * from the token and is already handled by the guard in the effect.
 *
 * Do not reintroduce a check on `is_cm_user`. Despite the name it is not a platform-staff flag: it
 * is set to true by ordinary user creation (self-signup owners and invited users) and is read by
 * nothing else in either application. Gating on it suppressed branding for exactly the users it
 * happened to be set on, which is the bug this comment exists to prevent.
 */

export interface BrandingContextValue extends ResolvedBranding {
  organizationName: string;
  logoUrl: string;
  favicon: string;
  isLoading: boolean;
}

const BrandingContext = createContext<BrandingContextValue>({
  ...platformBranding(),
  organizationName: '',
  logoUrl: '',
  favicon: '',
  isLoading: true,
});

export const useBranding = () => useContext(BrandingContext);

/** For the places that need a concrete colour rather than a CSS token. */
export const useBrand = () => useContext(BrandingContext).tokens;

const BRANDING_ENABLED = process.env.REACT_APP_ORG_BRANDING_ENABLED !== 'false';

/** How often to notice the session cookie appearing or disappearing. */
const SESSION_POLL_MS = 1000;

/**
 * Re-renders the provider when the auth cookie changes.
 *
 * `useJwtToken()` reads the cookie synchronously and does not subscribe to it, and this provider
 * sits above <App/>, so nothing below it can trigger a re-render here. Without this, signing in
 * would leave branding unresolved for the rest of the session — the cookie appears, but the
 * provider never looks again until a full page reload.
 */
function useSessionToken(): string | undefined {
  const [token, setToken] = useState<string | undefined>(() => Cookies.get('auth_token'));

  useEffect(() => {
    const id = window.setInterval(() => {
      const current = Cookies.get('auth_token');
      setToken((previous) => (previous === current ? previous : current));
    }, SESSION_POLL_MS);

    return () => window.clearInterval(id);
  }, []);

  return token;
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  // Subscribed to the cookie so sign-in and sign-out both re-resolve branding.
  useSessionToken();

  const authUser: any = useJwtToken();

  const organizationId: string | null = authUser?.user?.organization_id || null;
  const authToken: string | undefined = authUser?.token;

  const [branding, setBranding] = useState<ResolvedBranding>(
    () => (BRANDING_ENABLED && readCachedBranding(null)) || platformBranding()
  );
  const [meta, setMeta] = useState({ organizationName: '', logoUrl: '', favicon: '' });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!BRANDING_ENABLED) {
      applyBrandToDocument(platformBranding());
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const cached = readCachedBranding(organizationId);
    if (cached) {
      setBranding(cached);
      applyBrandToDocument(cached);
    }

    /*
     * Branding is only resolved for a signed-in user. The guest screens (join lobby, sign-in,
     * sign-up, activation, password reset, feedback) deliberately keep the platform look: the
     * branding endpoint requires a JWT like every other API, and a guest has none. Resolving by
     * custom domain instead would mean exposing an unauthenticated endpoint, which is not worth
     * it for a lookup that no organization can currently satisfy anyway.
     */
    if (!organizationId) {
      const platform = platformBranding();
      setBranding(platform);
      applyBrandToDocument(platform);
      setIsLoading(false);
      return;
    }

    const fetchBranding = getOrganizationAppearance(organizationId, authToken);

    fetchBranding
      .then((data) => {
        if (cancelled) return;

        const resolved = resolveBranding(data);
        setBranding(resolved);
        setMeta({
          organizationName: data.organizationName || '',
          logoUrl: data.logoUrl || '',
          favicon: data.favicon || '',
        });
        applyBrandToDocument(resolved);
        cacheBranding(organizationId || data.organizationId || null, resolved);
      })
      .catch(() => {
        // A guest on a host with no configured custom domain is the common case here, and a 404
        // is the expected answer. Platform branding is the correct outcome, not an error.
        if (!cancelled && !cached) applyBrandToDocument(platformBranding());
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId, authToken]);

  const value = useMemo<BrandingContextValue>(
    () => ({ ...branding, ...meta, isLoading }),
    [branding, meta, isLoading]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}
